import {Model} from '../Models';
import {Preconditions} from '../Errors/Preconditions';
import {UriBuilder} from './UriBuilder';
import {RequestResult} from './RequestResult';
import {APIError} from './APIError';
import {DataTransferObject} from '../DTOs';
import {BaseService} from './BaseService';
import {SessionStorageUtility} from '../Utilities/SessionStorageUtility';
import {SessionStorageObject} from '../Utilities/SessionStorageObject';

export class BaseModelService<T extends Model> extends BaseService {
  protected readonly modelClass: (new (data: any) => T);

  constructor(modelClass: (new (data: any) => T)) {
    super();
    this.modelClass = modelClass;
  }

  getUrlFor(id: string) {
    Preconditions.checkNotNull(id);
    Preconditions.checkNotEmpty(id);

    return new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(id)
      .build();
  }

  async get(id: string): Promise<T> {
    Preconditions.checkNotNull(id);
    Preconditions.checkNotEmpty(id);

    const url = this.getUrlFor(id);
    const result = await this.restClient.GET(url);
    if (result.response) {
      return new this.modelClass(result.response);
    }
    return this.throwAPIError(result.error);
  }

  async list(path = '', filters = {}): Promise<Array<T>> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(path)
      .getParams(filters)
      .build();
    const result = await this.restClient.GET(url);
    if (this.hasListResult(result)) {
      return this.getListResult(result).map(
        (instanceData: any) => new this.modelClass(instanceData),
      );
    }
    return this.throwAPIError(result.error);
  }

  async listCached<U extends SessionStorageObject>(path = '', filters = {}): Promise<U[]> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(path)
      .getParams(filters)
      .build();
    const cachedResult = SessionStorageUtility.loadList<U>(url, <U><unknown>new this.modelClass({}));
    if (cachedResult !== null) {
      return cachedResult;
    }

    const result = await this.restClient.GET(url);
    if (this.hasListResult(result)) {
      const listResult = this.getListResult(result);
      SessionStorageUtility.setList<U>(url, listResult);
      return listResult.map(
        (instanceData: any) => new this.modelClass(instanceData),
      );
    }
    return this.throwAPIError(result.error);
  }

  async create(transferObject: DataTransferObject): Promise<T> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .build();
    const result = await this.restClient.POST(url, transferObject, {'content-type': 'application/json'});
    if (result.response) {
      return new this.modelClass(result.response);
    }
    return this.throwAPIError(result.error);
  }

  async update(id: string, transferObject: DataTransferObject): Promise<T> {
    Preconditions.checkNotNull(id);
    Preconditions.checkNotEmpty(id);

    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(id)
      .build();
    const result = await this.restClient.PUT(url, transferObject, {'content-type': 'application/json'});
    if (result.response) {
      return new this.modelClass(result.response);
    }
    return this.throwAPIError(result.error);
  }

  async delete(id: string): Promise<boolean> {
    Preconditions.checkNotNull(id);
    Preconditions.checkNotEmpty(id);

    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(id)
      .build();
    const result = await this.restClient.DELETE(url);
    if (result.response) {
      return true;
    }
    return this.throwAPIError(result.error);
  }

  protected hasListResult(result: RequestResult) {
    return Boolean(result.response);
  }

  protected getListResult(result: RequestResult) {
    return result.response.results ? result.response.results : result.response;
  }

  protected throwAPIError(errorData): never {
    if (errorData.response) {
      if (errorData.response.errors) {
        throw new APIError(JSON.stringify(errorData.response.errors));
      }
      throw new APIError(JSON.stringify(errorData.response));
    }
    throw new APIError(JSON.stringify(errorData));
  }
}
